import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  Chip, // Add this import
} from "@mui/material";

import VisibilityIcon from "@mui/icons-material/Visibility";
import CancelIcon from "@mui/icons-material/Cancel";
import { useEffect, useState } from "react";
import ToastMessage from "./toast-message";
import axios from "axios";
import deleteFileFromCloud from "../utils/delete-file";
import { Download } from "@mui/icons-material";

const ApprovedArticles = ({ status }) => {
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [actionStatus, setActionStatus] = useState({ type: 0, message: "" });

  // Pagination state
  const [page, setPage] = useState(0);
  const [articlesPerPage] = useState(10); // Fixed at 10 per page

  const clearToastMessge = () => {
    setActionStatus({ type: 0, message: "" });
  };
  
  const deleteArticle = async () => {
    setOpenDeleteDialog(false);
    setLoading(true);
    const path = `wajls/articles/${selectedArticle.file_path}`;
    await deleteFileFromCloud(path);
    try {
      const payload = { id: selectedArticle.id };
      const response = await axios.post(
        "https://api.wajls.com/admin/v1/delete_article.php",
        payload
      );
      setActionStatus({
        type: response.data.success ? 1 : 0,
        message: response.data.message,
      });
      if (response.data.success) {
        setArticles((prev) =>
          prev.filter((article) => article.id !== selectedArticle.id)
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchArticles = async () => {
    if (status) {
      setFetching(true);
      try {
        const response = await axios.get(
          `https://api.wajls.com/admin/v1/get_articles.php?status=*`
        );
        setArticles(response.data.articles || []);
      } catch (error) {
        console.error("Error fetching articles:", error);
      } finally {
        setFetching(false);
      }
    }
  };

  const downloadInfo = (id) => {
    const article = articles[id];

    if (!article) return;

    const content = `
      ARTICLE DETAILS
      -------------------------
      Title: ${article.title}
      ID: ${article.id}
      Status: ${article.status}
      Publish Date: ${article.publish_date}

      AUTHOR DETAILS
      -------------------------
      Name: ${article.authors?.[0]?.name || "N/A"}
      Email: ${article.authors?.[0]?.email || "N/A"}

      CONTACT
      -------------------------
      Email: ${article.email}
      Phone: ${article.phone_number}

      PUBLICATION
      -------------------------
      Publisher ID: ${article.publisher_id}
      Blog Link: ${article.blog_link || "N/A"}

      FILE URL
      -------------------------
      ${article.url}
    `;

    const blob = new Blob([content], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `article_${article.id}.txt`;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };


  useEffect(() => {
    fetchArticles();
  }, [status]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginatedArticles = articles.slice(
    page * articlesPerPage,
    page * articlesPerPage + articlesPerPage
  );

  return (
    <Box sx={{ mt: 4, margin: "auto" }}>
      <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          {fetching ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                  <TableCell><Skeleton /></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                    <TableCell><Skeleton /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : articles.length > 0 ? (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell><strong>Title</strong></TableCell>
                    <TableCell><strong>Authors</strong></TableCell>
                    <TableCell><strong>Phone</strong></TableCell>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedArticles.map((manuscript, index) => (
                    <TableRow key={manuscript.id} hover>
                      <TableCell sx={{ maxWidth: 250 }}>
                       <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '100%'
                        }}
                      >
                        {manuscript.title}
                      </Typography>

                      </TableCell>
                      <TableCell>
                        {manuscript.authors.map((author, i) => (
                          <a
                            key={author.id}
                            href={`#`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              marginRight: 4,
                              color: "#1976d2",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                          >
                            {`${author.name} (${author.email})`}
                            {i < manuscript.authors.length - 1 && ","}
                          </a>
                        ))}
                      </TableCell>
                      <TableCell>{manuscript.phone_number}</TableCell>
                      <TableCell>{manuscript.email}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: 'center', gap: 1 }}>
                          {manuscript.status === 3 && (
                          <Chip
                            label="Pending Payment"
                            size="small"
                            color="warning"
                            sx={{ height: 20, fontSize: "0.7rem" }}
                          />
                        )}
                        
                          <Button
                            target="_blank"
                            variant="outlined"
                            size="small"
                            startIcon={<Download />}
                            onClick={() => downloadInfo(index)}
                          >
                            Info
                          </Button>
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            startIcon={<CancelIcon />}
                            onClick={() => {
                              setSelectedArticle(manuscript);
                              setOpenDeleteDialog(true);
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <TablePagination
                component="div"
                count={articles.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={articlesPerPage}
                rowsPerPageOptions={[]} // Hide rowsPerPage select
              />
            </>
          ) : (
            <Typography sx={{ p: 2 }}>No articles found</Typography>
          )}
        </Box>
      </Paper>

      {/* Delete Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Article</DialogTitle>
        <DialogContent>
          <Typography
            variant="body1"
            sx={{
              fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              fontSize: "16px",
              lineHeight: 1.6,
              color: "#333",
              mb: 2,
            }}
          >
            This action is final. Please ensure you've deleted the
            <a
              href="https://journal.wajls.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                marginRight: 4,
                color: "#1976d2",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              {" "}
              article
            </a>{" "}
            from the journal first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={deleteArticle}>
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Backdrop */}
      <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Toast Message */}
      {actionStatus.message && (
        <ToastMessage
          type={actionStatus.type}
          message={actionStatus.message}
          onClose={clearToastMessge}
        />
      )}
    </Box>
  );
};

export default ApprovedArticles;
